import React, { useMemo } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { NumericFormat, NumericFormatProps } from "react-number-format";

const getPreferredLocale = (): string => {
    if (typeof window === "undefined") return "en-SE";
    const culture = localStorage.getItem("culture") || "";
    const lang = localStorage.getItem("lang") || "";
    // Use the most specific valid locale first
    const pick = (l: string) =>
        l && Intl.NumberFormat.supportedLocalesOf([l]).length ? l : "";

    return pick(culture) || pick(lang) || navigator.language || "en-SE";
};

// Auto-detect locale decimal and thousand separators from a specific locale
const getLocaleSeparators = (locale: string) => {
    const parts = Intl.NumberFormat(locale).formatToParts(12345.67);
    // Some locales use NBSP or NARROW NBSP as the group separator. Normalize to a regular space.
    const rawGroup = parts.find(p => p.type === "group")?.value ?? ",";
    const thousandSeparator = rawGroup.replace(/\u00A0|\u202F/g, " ");
    const decimalSeparator = parts.find(p => p.type === "decimal")?.value ?? ".";
    return { decimalSeparator, thousandSeparator };
};

interface LocalizedNumberInputProps
    extends Omit<NumericFormatProps, "onValueChange" | "size" | "onBlur">,
    Pick<TextFieldProps, "size"> {
    label: string;
    value: number | null;
    onValueChange: (value: number | null) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    error?: boolean;
    helperText?: string;
    decimalScale?: number;
    allowNegative?: boolean;
    inputRef?: React.Ref<HTMLInputElement>;
    /** Optional override instead of localStorage/navigator, e.g. "sv-SE" */
    locale?: string;
}

const LocalizedNumberInput: React.FC<LocalizedNumberInputProps> = ({
    label,
    value,
    onValueChange,
    onBlur,
    error,
    helperText,
    decimalScale = 2,
    allowNegative = false,
    size = "medium",
    disabled,
    inputRef,
    locale,
    ...rest
}) => {
    const effectiveLocale = useMemo(
        () => (locale && locale.length ? locale : getPreferredLocale()),
        [locale]
    );
    const { decimalSeparator, thousandSeparator } = useMemo(
        () => getLocaleSeparators(effectiveLocale),
        [effectiveLocale]
    );

    return (
        <NumericFormat
            label={label}
            customInput={TextField}
            value={value ?? ""}
            onValueChange={(values) => {
                if (values.floatValue !== undefined) onValueChange(values.floatValue);
            }}
            fullWidth
            margin="normal"
            required
            error={error}
            helperText={helperText}
            allowNegative={allowNegative}
            decimalScale={decimalScale}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            inputProps={{ inputMode: "decimal" }}
            size={size}
            disabled={disabled}
            onBlur={onBlur}
            inputRef={inputRef}
        />
    );
};

export default LocalizedNumberInput;
